import { createApp } from 'vue'
import App from './App.vue'
import router from './router';
import { IonicVue } from '@ionic/vue';
import { datadogLogs } from '@datadog/browser-logs';
import { initBdash, restoreBdashUser, setBdashUser, setBdashReadyPromise } from '@/utils/bdash';
import { memberCard } from '@/actions/member-card';
import { initGA } from '@/utils/ga';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Theme variables */
import './theme/variables.scss';
import './theme/style.scss';

// Datadogの初期化オプション
type DatadogOptions = {
  clientToken: string;
  site: string;
  forwardErrorsToLogs?: boolean;
  service?: string;
  version?: string;
  sessionSampleRate?: number;
  host?: string;
  tags?: string[];
};

const datadogOptions: DatadogOptions = {
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN ?? '',
  site: 'ap1.datadoghq.com',
  forwardErrorsToLogs: true,
  service: 'shopworks-client',
  version: import.meta.env.VITE_APP_VERSION,
  sessionSampleRate: 10,
  host: window.location.hostname,
  tags: [
    'team:shopworks',
    'app:client'
  ]
};

try {
  datadogLogs.init(datadogOptions);
} catch (error) {
  console.error('Datadog initialization failed:', error);
}

// b→dash ワンタグの初期化
// page viewログにscrambleIDを載せるため、復元→初期化の順で実行する
// Storage未保存（初回起動など）の場合はmemberCard取得を待ってからinitBdashする
const bdashInitPromise = restoreBdashUser()
  .catch((error) => {
    console.warn('[b→dash] ユーザー復元に失敗しました:', error);
  })
  .then(async () => {
    if (window._bdash_user_id && window._bdash_barcode_number) return;
    try {
      const response = await memberCard();
      const user = response.data.user;
      setBdashUser(user.scrambled_id, user.app_barcode_number?.toString());
    } catch (error) {
      console.warn('[b→dash] memberCard取得失敗:', error);
    }
  })
  .finally(() => initBdash());
setBdashReadyPromise(bdashInitPromise as Promise<void>);

initGA();

// アプリケーションの作成とCapacitor関連の初期化
import { defineCustomElements } from '@ionic/pwa-elements/loader';
// Call the element loader after the platform has been bootstrapped
defineCustomElements(window);

const app = createApp(App)
  .use(IonicVue, { mode: 'ios' })
  .use(router);

// Vueのグローバルエラーハンドラ
app.config.errorHandler = (err: unknown, instance: any, info: string): void => {
  if (err instanceof Error) {
    datadogLogs.logger.error('Vue Component Error', {
      error: err,
      component: {
        name: instance?.$options?.name || 'anonymous',
        path: instance?.$options?.__file,
        info: info
      },
      context: {
        route: window.location.pathname,
        host: window.location.hostname,
        env: import.meta.env.VITE_APP_ENV || 'development'
      }
    }, err);
    console.error(err);
  }
};

router.isReady().then(() => {
  app.mount('#app');
});